import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

// The positions in widths of the tabs that fit one row, in their order, keeping the open one among them; the rest
// are listed under "More…", which then takes moreWidth of the row too.
export const tabsInRow = (widths, available, moreWidth, openPosition) => {
  if (widths.reduce((sum, width) => sum + width, 0) <= available) { return widths.map((_width, position) => position); }

  const shown = [];
  let used = moreWidth;
  while (shown.length < widths.length && used + widths[shown.length] <= available) {
    used += widths[shown.length];
    shown.push(shown.length);
  }
  if (openPosition == null || shown.includes(openPosition)) { return shown; }

  while (shown.length > 0 && used + widths[openPosition] > available) { used -= widths[shown.pop()]; }
  return shown.concat([openPosition]);
};

// How long the pointer may stray off "More…" before its dropdown closes.
export const MENU_CLOSE_DELAY_MILLISECONDS = 200;
// A click this soon after the hover that opened the dropdown is the same arrival -- a tap brings both -- so it keeps
// the dropdown open; a later click closes it.
export const CLICK_AFTER_HOVER_MILLISECONDS = 400;

const widthWithMargin = (element) => element.getBoundingClientRect().width + (parseFloat(window.getComputedStyle(element).marginRight) || 0);

// A section's tabs in one row; those that do not fit are listed under "More…", whose badge counts their selected
// members so no selection is out of sight. Every tab is rendered and measured before the row is painted: again
// whenever a label, a count, the open tab, the row's width or the fonts change.
const LayoutTabRow = ({ tabs, activeTab, onOpen, disabled }) => {
  const rowRef = useRef(null);
  const moreRef = useRef(null);
  const menuRef = useRef(null);
  const menuCloseTimer = useRef(null);
  const hoverOpenedAt = useRef(0);
  const [measurement, setMeasurement] = useState({ signature: null, positions: [] });
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAlignedLeft, setMenuAlignedLeft] = useState(false);
  const [resizes, setResizes] = useState(0);

  const signature = JSON.stringify([tabs.map((tab) => [tab.index, tab.label, tab.selectedCount]), activeTab, resizes]);
  const measuring = measurement.signature !== signature;

  useLayoutEffect(() => {
    if (!measuring) { return; }

    const elements = Array.from(rowRef.current.children);
    const moreElement = elements.pop();
    const moreBadge = moreElement.querySelector('.badge');
    const moreWidthWithBadge = widthWithMargin(moreElement);
    const moreWidth = moreWidthWithBadge - (moreBadge ? widthWithMargin(moreBadge) : 0);
    const widths = elements.map(widthWithMargin);
    const available = rowRef.current.clientWidth;
    const activePosition = tabs.findIndex((tab) => tab.index === activeTab);
    const openPosition = activePosition >= 0 ? activePosition : null;

    // "More…" takes its badge's width only when what it holds is selected.
    let positions = tabsInRow(widths, available, moreWidth, openPosition);
    if (tabs.some((tab, position) => !positions.includes(position) && tab.selectedCount > 0)) {
      positions = tabsInRow(widths, available, moreWidthWithBadge, openPosition);
    }
    setMeasurement({ signature, positions });
  }, [signature]);

  // The row's own width, not the window's: a scrollbar appearing, or a row first drawn hidden, changes it with no
  // window resize. A change of height alone is no reason to measure again.
  useEffect(() => {
    const row = rowRef.current;
    let lastWidth = row.clientWidth;
    const remeasureWhenWidthChanges = () => {
      if (row.clientWidth === lastWidth) { return; }

      lastWidth = row.clientWidth;
      setResizes((count) => count + 1);
    };
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(() => setResizes((count) => count + 1)); }
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', remeasureWhenWidthChanges);
      return () => { window.removeEventListener('resize', remeasureWhenWidthChanges); };
    }

    const observer = new ResizeObserver(remeasureWhenWidthChanges);
    observer.observe(row);
    return () => { observer.disconnect(); };
  }, []);

  const shown = measuring ? tabs : measurement.positions.map((position) => tabs[position]);
  const hidden = measuring ? [] : tabs.filter((_tab, position) => !measurement.positions.includes(position));
  // While measuring, "More…" carries a badge whenever anything is selected, so the badge's width is measured too.
  const hiddenSelectedCount = (measuring ? tabs : hidden).reduce((sum, tab) => sum + tab.selectedCount, 0);
  const moreShown = !measuring && hidden.length > 0;

  // A dropdown left open when "More…" went (every tab fits) would come back open with it.
  useEffect(() => {
    if (!measuring && !moreShown) { setMenuOpen(false); }
  }, [measuring, moreShown]);

  useEffect(() => {
    if (!menuOpen) { return undefined; }

    const closeOutside = (event) => {
      if (moreRef.current && !moreRef.current.contains(event.target)) { setMenuOpen(false); }
    };
    document.addEventListener('mousedown', closeOutside);
    return () => { document.removeEventListener('mousedown', closeOutside); };
  }, [menuOpen]);

  // The dropdown hangs left from "More…"'s right edge; near the left of a narrow screen it would start off it.
  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuAlignedLeft(false);
      return;
    }
    if (menuRef.current && menuRef.current.getBoundingClientRect().left < 0) { setMenuAlignedLeft(true); }
  }, [menuOpen, measuring]);

  useEffect(() => () => clearTimeout(menuCloseTimer.current), []);

  const openMenuOnHover = () => {
    clearTimeout(menuCloseTimer.current);
    if (!menuOpen) { hoverOpenedAt.current = Date.now(); }
    setMenuOpen(true);
  };

  const closeMenuSoon = () => {
    clearTimeout(menuCloseTimer.current);
    menuCloseTimer.current = setTimeout(() => setMenuOpen(false), MENU_CLOSE_DELAY_MILLISECONDS);
  };

  const toggleMenuOnClick = () => {
    clearTimeout(menuCloseTimer.current);
    setMenuOpen(!(menuOpen && Date.now() - hoverOpenedAt.current > CLICK_AFTER_HOVER_MILLISECONDS));
  };

  const openTab = (tab) => {
    setMenuOpen(false);
    onOpen(tab.index);
  };

  const badge = (count) => count > 0 && <span className="badge bgred"> {count}</span>;
  const label = (tab) => (tab.icon ? <><i className={`fa ${tab.icon}`} aria-hidden="true"></i> {tab.label}</> : tab.label);

  return (
    <div ref={rowRef} className={`shortcode_tabs shortcode_tabs_layout ${measuring ? 'is-measuring' : ''}`}>
      {shown.map((tab) => (
        <div key={`tab_${tab.index}`} onClick={() => openTab(tab)}
          className={`shortcode_tab_item_title ${disabled ? 'disabledForm' : ''} ${tab.index === activeTab ? 'inactivetab' : 'active'}`}>
          {label(tab)}
          {badge(tab.selectedCount)}
        </div>
      ))}

      {(measuring || moreShown) && (
        <div ref={moreRef} onClick={toggleMenuOnClick} onMouseEnter={openMenuOnHover} onMouseLeave={closeMenuSoon}
          className={`shortcode_tab_item_title active shortcode_tabs_layout_more ${disabled ? 'disabledForm' : ''}`}>
          {t('models.more')}… <i className="fa fa-caret-down" aria-hidden="true"></i>
          {badge(hiddenSelectedCount)}
          {menuOpen && moreShown && (
            <div ref={menuRef} className={`shortcode_tabs_layout_more_menu ${menuAlignedLeft ? 'is-aligned-left' : ''}`}>
              {hidden.map((tab) => (
                // Kept off "More…", which would take the click for its own.
                <div key={`more_${tab.index}`} onClick={(event) => { event.stopPropagation(); openTab(tab); }}
                  className="shortcode_tab_item_title active shortcode_tabs_layout_more_item">
                  {label(tab)}
                  {badge(tab.selectedCount)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LayoutTabRow;
